import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { MONTH_LABELS } from '../../../placeholders/periodSummaryUtils';
import {
  addBudgetSalesItem,
  deleteBudgetSalesItem,
  getBudgetCatalog,
  updateBudgetSalesItem,
} from '../../../../services/budgetAPI';
import { buildFormStyles, money, Pill, TreasuryDrawer } from '../treasury/treasuryUi';
import { flattenBudgetAccounts } from './BudgetAccountsPanel';
import { PartyFields, PartyHint } from './budgetPartyFields';

const RECURRENCE_OPTIONS = ['Mensual', 'Trimestral', 'Semestral', 'Anual', 'Puntual'];
const LINE_LABELS = { tcoat: 'T-COAT', upselling: 'Upselling' };
const CHANNEL_OPTIONS = [
  { code: 'B2B', label: 'B2B' },
  { code: 'B2G', label: 'B2G' },
  { code: 'B2B2C', label: 'B2B2C' },
];

const emptyForm = (year) => ({
  product_sku: '',
  channel: '',
  quantity: '',
  unit_price: '',
  amount: '',
  recurrence: 'Mensual',
  start_month: '1',
  end_month: '12',
  start_year: String(year || new Date().getFullYear()),
  end_year: String(year || new Date().getFullYear()),
  budget_account_id: '',
  chart_account_id: '',
  notes: '',
  supplier: '',
  payment_term: 'contado',
});

const monthYearLabel = (month, year) => `${MONTH_LABELS[(Number(month) || 1) - 1]} ${year || ''}`.trim();

const yearOptionsFor = (budgetYear, extraYears = []) => {
  const years = new Set();
  const base = Number(budgetYear) || new Date().getFullYear();
  for (let year = base - 5; year <= base + 5; year += 1) years.add(year);
  extraYears.forEach((year) => {
    const value = Number(year);
    if (value) years.add(value);
  });
  return Array.from(years).sort((a, b) => a - b);
};

const periodLabel = (item) => {
  const start = monthYearLabel(item.start_month, item.start_year);
  const end = monthYearLabel(item.end_month, item.end_year);
  if (item.recurrence === 'Puntual') return `${item.recurrence} · ${start}`;
  if (
    Number(item.start_month) === 1 &&
    Number(item.end_month) === 12 &&
    Number(item.start_year) === Number(item.end_year)
  ) {
    return item.start_year ? `${item.recurrence} · ${item.start_year}` : item.recurrence;
  }
  return `${item.recurrence} · ${start}–${end}`;
};

const parseNumber = (value) => Number(String(value ?? '').replace(',', '.')) || 0;

const BudgetIncomeStructurePanel = ({
  ui,
  isDarkMode = false,
  budget = null,
  budgetAccounts = [],
  canEdit = false,
  onChanged,
}) => {
  const styles = buildFormStyles(ui);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getBudgetCatalog();
        if (cancelled) return;
        setProducts(Array.isArray(payload?.products) ? payload.products : []);
      } catch {
        if (!cancelled) setProducts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const channels = useMemo(() => {
    const seen = new Set(CHANNEL_OPTIONS.map((row) => row.label));
    const extra = (budget?.sales_items || [])
      .map((item) => String(item.channel || '').trim())
      .filter((label) => label && !seen.has(label));
    extra.forEach((label) => seen.add(label));
    return [
      ...CHANNEL_OPTIONS,
      ...extra.map((label) => ({ code: label, label })),
    ];
  }, [budget]);

  const ingresoAccounts = useMemo(
    () => flattenBudgetAccounts(budgetAccounts).filter((row) => row.nature === 'ingreso'),
    [budgetAccounts]
  );

  const budgetYear = Number(budget?.year) || new Date().getFullYear();
  const yearOptions = yearOptionsFor(budgetYear, [form.start_year, form.end_year]);
  const computedAmount = parseNumber(form.quantity) && parseNumber(form.unit_price)
    ? Math.round(parseNumber(form.quantity) * parseNumber(form.unit_price) * 100) / 100
    : parseNumber(form.amount);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (budget?.sales_items || [])
      .filter((item) => (item.schedule_kind || 'recurring') !== 'variable')
      .filter((item) => {
        if (channelFilter !== 'all' && item.channel !== channelFilter) return false;
        if (!q) return true;
        return `${item.product_sku} ${item.product_name} ${item.channel} ${item.account_label} ${item.supplier}`.toLowerCase().includes(q);
      });
  }, [budget, search, channelFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(budgetYear));
    setError('');
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      product_sku: item.product_sku || '',
      channel: item.channel || '',
      quantity: item.quantity ? String(item.quantity) : '',
      unit_price: item.unit_price ? String(item.unit_price) : '',
      amount: String(item.amount ?? ''),
      recurrence: item.recurrence || 'Mensual',
      start_month: String(item.start_month || 1),
      end_month: String(item.end_month || 12),
      start_year: String(item.start_year || budgetYear),
      end_year: String(item.end_year || item.start_year || budgetYear),
      budget_account_id: String(item.budget_account_id || ''),
      chart_account_id: String(item.chart_account_id || ''),
      notes: item.notes || '',
      supplier: item.supplier || '',
      payment_term: item.payment_term || 'contado',
    });
    setError('');
    setShowForm(true);
  };

  const payloadFromForm = () => ({
    product_sku: form.product_sku,
    channel: form.channel,
    quantity: parseNumber(form.quantity),
    unit_price: parseNumber(form.unit_price),
    amount: computedAmount,
    schedule_kind: 'recurring',
    recurrence: form.recurrence,
    start_month: Number(form.start_month),
    end_month: Number(form.end_month),
    start_year: Number(form.start_year),
    end_year: Number(form.end_year),
    budget_account_id: form.budget_account_id ? Number(form.budget_account_id) : null,
    chart_account_id: form.chart_account_id ? Number(form.chart_account_id) : null,
    notes: form.notes,
    supplier: form.supplier,
    payment_term: form.payment_term || 'contado',
  });

  const handleSave = async () => {
    if (!budget?.id) return;
    setBusy(true);
    setError('');
    try {
      const payload = payloadFromForm();
      const next = editing
        ? await updateBudgetSalesItem(budget.id, editing.id, payload)
        : await addBudgetSalesItem(budget.id, payload);
      setShowForm(false);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la proyección.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar ${item.product_name} · ${item.channel}?`)) return;
    setBusy(true);
    setError('');
    try {
      const next = await deleteBudgetSalesItem(budget.id, item.id);
      onChanged?.(next);
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la proyección.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: ui.panel, border: `1px solid ${ui.border}`, borderRadius: 10, padding: 12 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>Ingresos recurrentes</h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
          Cantidad y precio fijos por período. El monto se expande según la periodicidad y suma en Ingresos del resumen.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar producto o canal…"
            style={{ ...styles.input, width: 240 }}
          />
          <select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)} style={{ ...styles.input, width: 220 }}>
            <option value="all">Todos los canales</option>
            {channels.map((option) => (
              <option key={option.label} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        {canEdit ? (
          <button type="button" style={styles.primaryBtn} onClick={openCreate}>
            <Plus size={14} /> Agregar proyección
          </button>
        ) : null}
      </div>
      {error && !showForm ? <p style={{ margin: '0 0 10px', color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead>
            <tr>
              {['Producto', 'Canal', 'Periodicidad', 'Cant.', 'Precio', 'Monto', 'Anual', 'Cuenta', ''].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: ['Cant.', 'Precio', 'Monto', 'Anual'].includes(label) ? 'right' : 'left',
                    padding: 8,
                    borderBottom: `1px solid ${ui.border}`,
                    fontSize: 11,
                    color: ui.muted,
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((item) => (
              <tr key={item.id}>
                <td style={{ padding: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: ui.text }}>{item.product_name}</div>
                  <div style={{ fontSize: 11, color: ui.muted }}>{item.product_sku}</div>
                  <PartyHint item={item} ui={ui} />
                  <Pill label={LINE_LABELS[item.product_line] || item.product_line} tone="info" isDarkMode={isDarkMode} />
                </td>
                <td style={{ padding: 8, fontSize: 12, fontWeight: 700 }}>{item.channel}</td>
                <td style={{ padding: 8, fontSize: 12 }}>{periodLabel(item)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{item.quantity || '—'}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{item.unit_price ? money(item.unit_price) : '—'}</td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700, fontSize: 12 }}>{money(item.amount)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontSize: 12 }}>{money(item.budget_total)}</td>
                <td style={{ padding: 8, fontSize: 12, color: ui.text, whiteSpace: 'nowrap' }}>
                  {item.account_label || item.account_code || '—'}
                </td>
                <td style={{ padding: 8, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {canEdit ? (
                    <>
                      <button type="button" style={styles.ghostBtn} onClick={() => openEdit(item)}>
                        <Pencil size={13} />
                      </button>
                      <button type="button" style={styles.ghostBtn} onClick={() => handleDelete(item)} disabled={busy}>
                        <Trash2 size={13} />
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!rows.length ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, textAlign: 'center', color: ui.muted, fontSize: 13 }}>
                  Agrega proyecciones de venta por producto y canal para que sumen en el resumen.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showForm ? (
        <TreasuryDrawer
          ui={ui}
          title={editing ? 'Editar proyección' : 'Nueva proyección de venta'}
          subtitle="Cantidad × precio da el monto de cada período. Elige mes y año de inicio y término."
          error={error}
          saving={busy}
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        >
          <label style={styles.label}>
            Producto
            <select
              value={form.product_sku}
              onChange={(event) => setForm((prev) => ({ ...prev, product_sku: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona un producto</option>
              {products.map((option) => (
                <option key={option.product_sku} value={option.product_sku}>
                  {LINE_LABELS[option.product_line] || option.product_line} · {option.product_sku} · {option.product_name}
                </option>
              ))}
            </select>
          </label>
          <label style={styles.label}>
            Canal
            <select value={form.channel} onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))} style={styles.input}>
              <option value="">Selecciona un canal</option>
              {CHANNEL_OPTIONS.map((option) => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
              {form.channel && !CHANNEL_OPTIONS.some((option) => option.label === form.channel) ? (
                <option value={form.channel}>{form.channel}</option>
              ) : null}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Cantidad por período
              <input value={form.quantity} onChange={(event) => setForm((prev) => ({ ...prev, quantity: event.target.value }))} style={styles.input} />
            </label>
            <label style={styles.label}>
              Precio unitario
              <input value={form.unit_price} onChange={(event) => setForm((prev) => ({ ...prev, unit_price: event.target.value }))} style={styles.input} />
            </label>
          </div>
          <label style={styles.label}>
            Monto por período
            <input
              value={form.quantity && form.unit_price ? String(computedAmount) : form.amount}
              onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value, quantity: '', unit_price: '' }))}
              style={styles.input}
            />
          </label>
          <label style={styles.label}>
            Periodicidad
            <select value={form.recurrence} onChange={(event) => setForm((prev) => ({ ...prev, recurrence: event.target.value }))} style={styles.input}>
              {RECURRENCE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={styles.label}>
              Desde
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                <select value={form.start_month} onChange={(event) => setForm((prev) => ({ ...prev, start_month: event.target.value }))} style={styles.input}>
                  {MONTH_LABELS.map((label, index) => (
                    <option key={`start-${label}`} value={String(index + 1)}>
                      {label}
                    </option>
                  ))}
                </select>
                <select value={form.start_year} onChange={(event) => setForm((prev) => ({ ...prev, start_year: event.target.value }))} style={styles.input}>
                  {yearOptions.map((year) => (
                    <option key={`start-year-${year}`} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </label>
            {form.recurrence === 'Puntual' ? null : (
              <label style={styles.label}>
                Hasta
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 88px', gap: 8 }}>
                  <select value={form.end_month} onChange={(event) => setForm((prev) => ({ ...prev, end_month: event.target.value }))} style={styles.input}>
                    {MONTH_LABELS.map((label, index) => (
                      <option key={`end-${label}`} value={String(index + 1)}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select value={form.end_year} onChange={(event) => setForm((prev) => ({ ...prev, end_year: event.target.value }))} style={styles.input}>
                    {yearOptions.map((year) => (
                      <option key={`end-year-${year}`} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            )}
          </div>
          <label style={styles.label}>
            Clasificación (cuenta de ingreso)
            <select
              value={form.budget_account_id}
              onChange={(event) => setForm((prev) => ({ ...prev, budget_account_id: event.target.value }))}
              style={styles.input}
            >
              <option value="">Selecciona una cuenta de ingreso</option>
              {ingresoAccounts.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.optionLabel}
                </option>
              ))}
            </select>
            {!ingresoAccounts.length ? (
              <span style={{ fontSize: 11, color: ui.muted }}>Crea una cuenta de naturaleza Ingreso en la píldora Cuentas.</span>
            ) : null}
          </label>
          <PartyFields form={form} setForm={setForm} styles={styles} />
          <label style={styles.label}>
            Notas
            <input value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} style={styles.input} />
          </label>
        </TreasuryDrawer>
      ) : null}
    </div>
  );
};

export default BudgetIncomeStructurePanel;
